import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TokenService } from 'lib/apis/TokenService';
import { redirectToVendorConnectOrFallback } from 'shared/lib/VendorConnectRedirect';
/**
 * VendorConnectRedirectPage
 *
 * A reusable component that redirects to Vendor Connect with an optional path endpoint or external URL.
 * Used when certain Pearl pages should redirect to their Vendor Connect equivalents.
 *
 * For authenticated users: redirects with tokens to specific endpoint (e.g., '/business', '/dashboard')
 * For unauthenticated users: redirects to base Vendor Connect URL without tokens
 *
 * Returns null to prevent any page content from flashing before the redirect happens,
 * ensuring a smooth user experience.
 *
 * @param {Object} props
 * @param {string} props.path - The endpoint path to append to the vendor connect URL (e.g., '/business', '/dashboard')
 *                              or a full external URL (e.g., 'https://connect.davidsbridal.com/#faqs')
 * @param {boolean} props.external - Set to true if path is a full external URL
 */
const VendorConnectRedirectPage = ({ path = '', external = false }) => {
    const navigate = useNavigate();
    const tokenService = TokenService();

    useEffect(() => {
        console.log('[VendorConnectRedirectPage] initiating redirect', { path, external });

        // Handle external URLs directly
        if (external) {
            window.location.assign(path);
            return;
        }

        // Check if user has valid tokens
        const accessToken = tokenService?.getAccessToken?.();
        const refreshToken = tokenService?.getRefreshToken?.();
        const idToken = tokenService?.getIdToken?.();
        const baseUrl = process.env.REACT_APP_VENDORCONNECT_URL;

        // If user is not authenticated, redirect to base Vendor Connect URL
        if (!accessToken || !refreshToken || !idToken) {
            console.log(
                '[VendorConnectRedirectPage] unauthenticated user, redirecting to base URL',
                {
                    baseUrl,
                }
            );
            if (baseUrl) {
                window.location.assign(baseUrl);
            }
            return;
        }

        // Handle Vendor Connect internal paths with token sharing for authenticated users
        redirectToVendorConnectOrFallback(tokenService, navigate, path);
    }, [navigate, tokenService, path, external]);

    // Return null to prevent any page flash before redirect
    // The redirect happens immediately in useEffect
    return null;
};

export { VendorConnectRedirectPage };
export default VendorConnectRedirectPage;
